import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { LOGO_PATH, LOGO_ALT } from '@/lib/brand'
import { StoreImage } from '@/store/components/StoreImage'

interface LogoProps {
  collapsed?: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
  showText?: boolean
  /** Storefront vs admin subtitle */
  variant?: 'store' | 'admin'
  /** Gold gradient ring around the logo mark */
  gradientBorder?: boolean
  /** Wrap icon + name in a home link */
  linkTo?: string
}

const imageSizes = {
  sm: 'h-11 w-11',
  md: 'h-14 w-14',
  lg: 'h-28 w-28',
}

const textSizes = {
  sm: { title: 'text-base', sub: 'text-[9px]' },
  md: { title: 'text-lg', sub: 'text-[10px]' },
  lg: { title: 'text-2xl', sub: 'text-xs' },
}

const subtitles = {
  store: 'Luxury Attars',
  admin: 'Luxury Attars — Admin',
}

export function Logo({
  collapsed,
  className,
  size = 'md',
  showText = true,
  variant = 'admin',
  gradientBorder = false,
  linkTo,
}: LogoProps) {
  const displayText = showText && !collapsed

  const logoMark =
    variant === 'store' ? (
      <StoreImage
        src={LOGO_PATH}
        alt={LOGO_ALT}
        priority
        objectFit="contain"
        spinnerSize="sm"
        containerClassName={cn(
          'shrink-0 rounded-full overflow-hidden bg-luxury-950',
          gradientBorder ? 'h-full w-full' : imageSizes[size]
        )}
        className={cn(
          'h-full w-full object-contain drop-shadow-[0_2px_12px_rgba(212,175,55,0.25)]'
        )}
      />
    ) : (
      <img
        src={LOGO_PATH}
        alt={LOGO_ALT}
        className={cn(
          'shrink-0 rounded-full object-contain bg-luxury-950 drop-shadow-[0_2px_12px_rgba(212,175,55,0.25)]',
          gradientBorder ? 'h-full w-full' : imageSizes[size]
        )}
      />
    )

  const logoImage = gradientBorder ? (
    <div className={cn('logo-gradient-ring shrink-0', imageSizes[size])}>
      <div className="h-full w-full overflow-hidden rounded-full bg-luxury-950">
        {logoMark}
      </div>
    </div>
  ) : (
    logoMark
  )

  const content = (
    <div className={cn('flex items-center gap-3', className)}>
      {logoImage}
      {displayText && (
        <div className="min-w-0">
          <span
            className={cn(
              'block font-display font-bold leading-tight gold-gradient-text',
              textSizes[size].title
            )}
          >
            Bazm-e-Musk
          </span>
          {size !== 'sm' && (
            <span
              className={cn(
                'block text-gold-400/80 tracking-wide uppercase',
                textSizes[size].sub
              )}
            >
              {subtitles[variant]}
            </span>
          )}
        </div>
      )}
    </div>
  )

  if (linkTo) {
    return (
      <Link
        to={linkTo}
        aria-label="Bazm-e-Musk home"
        className="inline-flex shrink-0 transition-opacity hover:opacity-90"
      >
        {content}
      </Link>
    )
  }

  return content
}
